import axios from "axios";

import integrations from "@/static/integrations";
import PersonalRequestDTO from "@/DTOs/personal";
import BankRequestDTO from "@/DTOs/bank";
import EstablishmentRequestDTO from "../../DTOs/establishment";
import AddressRequestDTO from "../../DTOs/address";
import GroupRequestDTO from "../../DTOs/group";
import ContactRequestDTO from "../../DTOs/contact";
import { fileTypeToFormKey } from "@/utils/fileFormKey";

const isDevelopment = (process.env.NODE_ENV === "development");

const ownersStructure = {
	id: "",
	fullName: "",
	nationality: "",
	postalCode: "",
	street: "",
	number: "",
	complement: "",
	city: "",
	state: "",
	country: "",
	percent: "",
	documentOrder: [],
};

const representativeStructure = {
	id: "",
	fullName: "",
	role: "",
	email: "",
	linkedin: "",
	cpf: "",
	rg: "",
	birthday: "",
	documentOrder: [],
};

const contractStructure = {
	id: "",
	fullName: "",
	email: "",
	postalCode: "",
	street: "",
	number: "",
	city: "",
	state: "",
	country: "",
	complement: "",
};

const channelStructure = {
	id: "",
	fullName: "-",
	phone: "",
	email: "",
	url: "",
};

const contactStructure = {
	id: "",
	fullName: "",
	phone: "",
	email: "",
	url: "",
};

const politicalStructure = {
	id: "",
	fullName: "",
	role: "",
	country: "",
};

const passwordEncryption = 22;
const baseUrl = process.env.VUE_APP_BASE_URL;

const multipartHeader = {
	headers: {
		"content-type": "multipart/form-data",
	},
};

const cloneObject = (object) => {
	return Object.assign({}, object);
};

/**
 * Gera um nome único para o arquivo adicionando sufixo numérico (1), (2), etc. se necessário
 * @param {string} originalFileName - Nome original do arquivo
 * @param {Array} existingFiles - Array de arquivos existentes para verificar duplicatas
 * @param {Function} nameExtractor - Função para extrair o nome do arquivo do objeto
 * @returns {string} - Nome único do arquivo
 */
const generateUniqueFileName = (originalFileName, existingFiles, nameExtractor = (item) => item.name) => {
	const fileExtension = originalFileName.substring(originalFileName.lastIndexOf('.'));
	const fileNameWithoutExt = originalFileName.substring(0, originalFileName.lastIndexOf('.'));

	let uniqueName = originalFileName;
	let counter = 1;

	while (existingFiles.some(file => nameExtractor(file) === uniqueName)) {
		uniqueName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
		counter++;
	}

	return uniqueName;
};

const module = {
	state: () => ({
		step: 1,
		loadingButton: false,
		acceptedPolicy: false,
		personal: {
			id: 0,
			fullName: "",
			cpf: "",
			taxId: "",
			phone: "",
			email: "",
			access_code: "",
		},
		establishment: {
			company: "",
			company_fantasy: "",
			website: "",
			product: "",
			sites: [],
			age: "",
			taxId: "",
			type: "",
			product_category: "",
			product_description: "",
			product_shipping: "",
			sameAddress: true,
			hasGroup: false,
			exposed: false,
			agree: false,
			group: {
				name: "",
				taxId: "",
			},
			address: [
				{
					street: "",
					postalCode: "",
					number: "",
					complement: "",
					city: "",
					state: "",
					country: "",
				},
				{
					street: "",
					postalCode: "",
					number: "",
					complement: "",
					city: "",
					state: "",
					country: "",
				},
			],
		},
		bankAccount: {
			name: "",
			taxId: "",
			bank: "",
			number: "",
			agency: "",
			account: "",
			selectedBank: "",
		},
		ownersAndContacts: {
			owner: [cloneObject(ownersStructure)],
			representative: [cloneObject(representativeStructure)],
			contract: [cloneObject(contractStructure)],
			billing: [cloneObject(contactStructure)],
			technical: [cloneObject(contactStructure)],
			emergency: [cloneObject(contactStructure)],
			channel: [cloneObject(channelStructure)],
			support: [cloneObject(contactStructure)],
			refund: [cloneObject(contactStructure)],
			political: [cloneObject(politicalStructure)],
		},
		pci: {
			integration: "",
			conformity: true,
		},
		documents: {
			cpf: [],
			addressPartners: [],
			taxId: [],
			address: [],
			secondAddress: [],
			contract: [],
			socialContract: [],
			pci: [],
			representative: [],
		},
		uploadedDocuments: {
			cpf: [],
			addressPartners: [],
			taxId: [],
			address: [],
			secondAddress: [],
			contract: [],
			socialContract: [],
			pci: [],
			representative: [],
			kyc: [],
		},
		pendingFiles: {
			cpf: [],
			addressPartners: [],
			taxId: [],
			address: [],
			secondAddress: [],
			contract: [],
			socialContract: [],
			pci: [],
			representative: [],
			kyc: [],
		},
		pendingDeletes: {
			cpf: [],
			addressPartners: [],
			taxId: [],
			address: [],
			secondAddress: [],
			contract: [],
			socialContract: [],
			pci: [],
			representative: [],
			kyc: [],
		},
		confirmModal: {
			form: "",
			key: "",
			type: null,
			formKey: 1,
			salesforceContactId: "",
		},
		showSalesforceGetError: false,
		showSalesforcePostError: false,
		salesforcePostErrorMessage: "",
	}),
	actions: {
		async checkToken({ state, commit }) {
			let formData = new FormData();

			formData.append("email", state.personal.email);
			formData.append("access_code", state.personal.access_code);

			const res = await axios
				.post(baseUrl + "?class=Personal&method=checkAccessToken", formData, multipartHeader)
				.catch((e) => {
					console.error(e.message);
				});

			if (res.status !== 200 || !(res.data > 0)) {
				return false;
			}

			sessionStorage.setItem("pid", btoa(res.data * passwordEncryption));
			commit("setPersonal", {
				key: "id",
				value: res.data,
			});

			return true;
		},
		async resendToken({ state }) {
			let formData = new FormData();

			formData.append("email", state.personal.email);
			formData.append("cnpj", state.personal.taxId);

			const res = await axios
				.post(baseUrl + "?class=Personal&method=resendAccessToken", formData, multipartHeader)
				.catch((e) => {
					console.error(e.message);
				});

			return res.status === 200 && res.data > 0;
		},
		async submitForm({ state, dispatch }, form) {
			// Preserva pendingDeletes antes de deletar, pois será usado para filtrar arquivos no documentOrder
			const preservedPendingDeletes = {
				cpf: [...(state.pendingDeletes.cpf || [])],
				addressPartners: [...(state.pendingDeletes.addressPartners || [])],
				representative: [...(state.pendingDeletes.representative || [])]
			};

			// Para o formulário de contatos (case 2), salva os contatos primeiro e depois os arquivos
			// Para outros formulários, mantém a ordem original (arquivos primeiro)
			if (form === 2) {
				await dispatch("deletePendingFiles");

				// Salva os contatos primeiro (sem os arquivos pendentes ainda)
				// Os arquivos serão salvos depois e associados aos contatos
			} else {
				await dispatch("sendPendingFiles");
				await dispatch("deletePendingFiles");
			}

			let formData = new FormData();
			let controller = "Form";
			let method = "";
			let kycUuid = sessionStorage.getItem("kyc_uuid") ?? null;

			switch (form) {
				case 0:
					formData.append("id", state.personal.id);
					formData.append("personal_id", sessionStorage.getItem("pid"));
					formData.append("reset_token", state.step == 0 ? 0 : 1);

					formData.append("kyc_uuid", kycUuid);
					formData.append("fullname", state.personal.fullName);
					formData.append("cpf", state.personal.cpf);
					formData.append("phone", state.personal.phone);
					formData.append("email", state.personal.email);
					formData.append("cnpj", state.personal.taxId);
					formData.append("step", state.step);
					formData.append(
						"account_manager_abbreviation",
						localStorage.getItem("account_manager_abbreviation"),
					);

					controller = "Personal";
					method = "savePersonalData";
					break;
				case 1:
					formData.append("personal_id", sessionStorage.getItem("pid"));
					formData.append("step", state.step);

					formData.append("kyc_uuid", kycUuid);
					formData.append("company", state.establishment.company);
					formData.append("company_fantasy", state.establishment.company_fantasy);

					formData.append("website", state.establishment.website);
					formData.append("product", state.establishment.product);
					formData.append("age", state.establishment.age);
					formData.append("cnpj", "");
					formData.append("type", state.establishment.type);
					formData.append(
						"sites",
						state.establishment.sites.length >= 1 ? state.establishment.sites.join(";") : "",
					);

					if (state.establishment.hasGroup) {
						formData.append("group_name", state.establishment.group.name);
						formData.append("group_cnpj", state.establishment.group.taxId);
					}

					formData.append("product_category", state.establishment.product_category);

					if (state.establishment.product_description) {
						formData.append("product_description", state.establishment.product_description);
					}

					formData.append("product_shipping", state.establishment.product_shipping);

					formData.append("ma_cep", state.establishment.address[0].postalCode);
					formData.append("ma_street", state.establishment.address[0].street);
					formData.append("ma_number", state.establishment.address[0].number);
					formData.append("ma_complement", state.establishment.address[0].complement);
					formData.append("ma_city", state.establishment.address[0].city);
					formData.append("ma_state", state.establishment.address[0].state);
					formData.append("ma_country", state.establishment.address[0].country);

					if (!state.establishment.sameAddress) {
						formData.append("sa_cep", state.establishment.address[1].postalCode);
						formData.append("sa_street", state.establishment.address[1].street);
						formData.append("sa_number", state.establishment.address[1].number);
						formData.append("sa_complement", state.establishment.address[1].complement);
						formData.append("sa_city", state.establishment.address[1].city);
						formData.append("sa_state", state.establishment.address[1].state);
						formData.append("sa_country", state.establishment.address[1].country);
					}

					controller = "Establishment";
					method = "saveEstablishmentData";
					break;
				case 2:
					formData.append("personal_id", sessionStorage.getItem("pid"));
					formData.append("step", state.step);

					formData.append("kyc_uuid", kycUuid);
					state.ownersAndContacts.owner.forEach((owner, index) => {
						const ownerFields = [
							"id",
							"fullName",
							"nationality",
							"postalCode",
							"street",
							"number",
							"complement",
							"city",
							"state",
							"country",
							"percent",
							"cpf"
						];

						ownerFields.forEach((field) => {
							formData.append(`owner_${field}_${index}`, owner[field]);
						});

						const documentOrder = owner.documentOrder || [];
						const pendingDocumentOrder = owner.pendingDocumentOrder || [];
						// Usa a cópia preservada do pendingDeletes antes da deleção
						const pendingDeletes = preservedPendingDeletes.cpf.concat(preservedPendingDeletes.addressPartners);

						const savedFiles = documentOrder
							.filter(item => item.ownerIndex === index && (item.form === "cpf" || item.form === "address_partners"))
							.map(item => {
								return {
									id: item.id || item.salesforceFileId,
									name: item.name,
									ownerIndex: index,
									form: item.form,
								}
							});

						const pendingFiles = pendingDocumentOrder
							.filter(item => item.ownerIndex === index && (item.form === "cpf" || item.form === "address_partners"))
							.map(item => {
								return {
									name: item.name,
									ownerIndex: index,
									form: item.form,
								}
							});

						const allFiles = [...savedFiles, ...pendingFiles];

						const filesToSend = allFiles.filter(file => {
							if (file) {
								const isDeleted = pendingDeletes.some(
									deleteInfo => {

										return deleteInfo.name === file.name
									}

								);
								return !isDeleted;
							}
							return true;
						});

						if (filesToSend.length > 0) {
							formData.append(`owner_cpf_files_${index}`, JSON.stringify(filesToSend));
						} else {
							formData.append(`owner_cpf_files_${index}`, '[]');
						}
					});

					state.ownersAndContacts.representative.forEach((representative, index) => {
						const fields = ["id", "fullName", "role", "email", "linkedin", "cpf", "rg", "birthday"];

						fields.forEach((field) => {
							formData.append(`representative_${field}_${index}`, representative[field] || "");
						});

						const documentOrder = representative.documentOrder || [];
						const pendingDocumentOrder = representative.pendingDocumentOrder || [];
						const pendingDeletes = preservedPendingDeletes.representative || [];

						const savedFiles = documentOrder
							.filter(item => item.representativeIndex === index && item.form === "representative")
							.map(item => {
								return {
									id: item.id || item.salesforceFileId,
									name: item.name,
									representativeIndex: index,
									form: item.form,
								}
							});

						const pendingFiles = pendingDocumentOrder
							.filter(item => item.representativeIndex === index && item.form === "representative")
							.map(item => {
								return {
									name: item.name,
									representativeIndex: index,
									form: item.form,
								}
							});

						const allFiles = [...savedFiles, ...pendingFiles];


						const filesToSend = allFiles.filter(file => {
							if (file) {
								const isDeleted = pendingDeletes.some(
									deleteInfo => {
										return deleteInfo.name === file.name
									}
								);
								return !isDeleted;
							}
							return true;
						});

						if (filesToSend.length > 0) {
							formData.append(`representative_files_${index}`, JSON.stringify(filesToSend));
						} else {
							formData.append(`representative_files_${index}`, '[]');
						}
					});

					state.ownersAndContacts.contract.forEach((contract, index) => {
						const fields = [
							"id",
							"fullName",
							"email",
							"postalCode",
							"street",
							"number",
							"city",
							"state",
							"country",
							"complement",
							"cpf"
						];

						fields.forEach((field) => {
							formData.append(`contract_${field}_${index}`, contract[field]);
						});
					});

					const contactTypes = ["billing", "technical", "emergency", "channel", "support", "refund"];

					contactTypes.forEach((type) => {
						state.ownersAndContacts[type].forEach((contact, index) => {
							formData.append(`${type}_id_${index}`, contact.id || "");
							formData.append(`${type}_fullname_${index}`, type === "channel" ? contact.fullName : "");
							formData.append(`${type}_phone_${index}`, contact.phone || "");
							formData.append(`${type}_email_${index}`, contact.email || "");
							formData.append(`${type}_url_${index}`, contact.url || "");
						});
					});

					state.ownersAndContacts.political.forEach((political, index) => {
						if (political.fullName) {
							formData.append("political_id_" + index, political.id);
							formData.append("political_fullName_" + index, political.fullName);
							formData.append("political_role_" + index, political.role);
							formData.append("political_country_" + index, political.country);
						}
					});

					controller = "Contacts";
					method = "saveContactsData";
					break;
				case 3:
					formData.append("personal_id", sessionStorage.getItem("pid"));
					formData.append("step", state.step);

					formData.append("kyc_uuid", kycUuid);
					formData.append("integration", state.pci.integration.text);
					formData.append("conformity", state.pci.conformity);

					controller = "Integration";
					method = "saveIntegrationData";
					break;
				case 4:
					formData.append("personal_id", sessionStorage.getItem("pid"));
					formData.append("step", state.step);

					formData.append("kyc_uuid", kycUuid);
					formData.append("name", state.bankAccount.name);
					formData.append("cnpj", state.bankAccount.taxId);
					formData.append("bank", state.bankAccount.bank);
					formData.append("number", state.bankAccount.number);
					formData.append("agency", state.bankAccount.agency);
					formData.append("account", state.bankAccount.account);

					controller = "Bank";
					method = "saveBankAccount";
					break;
			}

			const res = await axios
				.post(baseUrl + "?class=" + controller + "&method=" + method, formData, multipartHeader)
				.catch((err) => {
					const errorMessage = err?.response?.data?.ERRO ?? "";

					this.commit("setSalesforcePostErrorMessage", errorMessage);
					this.commit("setShowSalesforcePostError", true);
					this.commit("setLoadingButton", false);

					if (isDevelopment === true) {
						console.error(err);
					}
				});

			if (res.status !== 200) {
				return false;
			}

			// Para o formulário de contatos, salva os arquivos depois de salvar os contatos
			if (form === 2) {
				await dispatch("sendPendingFiles");
			}

			if (method === "savePersonalData" && state.step !== 0) {
				sessionStorage.removeItem("pid");
			}

			this.commit("setSalesforcePostErrorMessage", "");
			this.commit("setShowSalesforcePostError", false);

			return res;
		},
		async getPersonalData({ commit, state }) {
			let formData = new FormData();

			formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
			formData.append("step", state.step);

			const res = await axios
				.post(baseUrl + "?class=Personal&method=getPersonalData", formData, multipartHeader)
				.catch((err) => {
					this.commit("setShowSalesforceGetError", true);
					this.commit("setLoadingButton", false);

					if (isDevelopment === true) {
						console.error(err);
					}
				});

			if (res.status !== 200) {
				return;
			}

			if (res.data.error) {
				throw new Error(res.data.error);
			}

			const personalData = res.data;

			const personalRequestDTO = new PersonalRequestDTO({
				id: personalData?.Personal_Contact__r?.Id ?? "",
				fullName: personalData?.Personal_Contact__r?.Name ?? "",
				cpf: personalData?.Personal_Contact__r?.Registration_Number__c ?? "",
				phone: personalData?.Personal_Contact__r?.Phone ?? "",
				email: personalData?.Personal_Contact__r?.Email ?? "",
				taxId: personalData?.Personal_Contact__r?.KYC_Fiscal_Code__c ?? "",
				step: personalData?.Stage_Number__c ?? "",
			});

			Object.entries(personalRequestDTO).forEach(([key, value]) => {
				if (key == "taxId") {
					commit("setEstablishment", {
						key,
						value,
					});
				}

				commit("setPersonal", {
					key,
					value,
				});
			});

			commit("setStep", parseInt(personalRequestDTO.step));
		},
		async getEstablishmentData({ commit, state }) {
			let formData = new FormData();

			formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
			formData.append("step", state.step);

			const res = await axios
				.post(baseUrl + "?class=Establishment&method=getEstablishmentData", formData, multipartHeader)
				.catch((err) => {
					this.commit("setShowSalesforceGetError", true);
					this.commit("setLoadingButton", false);

					if (isDevelopment === true) {
						console.error(err);
					}
				});

			if (res.status !== 200) {
				return;
			}

			const establishmentData = res.data;

			const establishmentRequestDTO = new EstablishmentRequestDTO({
				company: establishmentData?.Legal_Name__c ?? "",
				company_fantasy: establishmentData?.Trade_Name__c ?? "",
				website: establishmentData?.Company_URL__c ?? "",
				product: establishmentData?.Store_URL__c ?? "",
				age: establishmentData?.Founding_Date__c ?? "",
				product_category: establishmentData?.Service_Category__c ?? "",
				product_description: establishmentData?.Service_Description__c ?? "",
				product_shipping: establishmentData?.Service_Shipping__c ?? "",
				establishment_type: establishmentData?.Company_Type__c ?? "",
				sameAddress: establishmentData?.Unique_Address__c ?? true,
				hasGroup: establishmentData?.Corporate_Group__c ?? false,
			});

			Object.entries(establishmentRequestDTO).forEach(([key, value]) => {
				commit("setEstablishment", {
					key: key == "establishment_type" ? "type" : key,
					value: key == "age" ? value.split("-").reverse().join("/") : value,
				});
			});

			if (establishmentRequestDTO.product) {
				let urlList = establishmentRequestDTO.product.split(";");

				commit("setEstablishment", {
					key: "product",
					value: urlList[0],
				});

				if (urlList.length >= 2) {
					for (let i = 1; i < urlList.length; i++) {
						state.establishment.sites.push(urlList[i]);
					}
				}
			}

			const addressData = res.data;

			const addressRequestDTO = new AddressRequestDTO({
				postalCode: addressData?.Postal_Code__c ?? "",
				street: addressData?.Address__c ?? "",
				number: addressData?.Street_Number__c ?? "",
				complement: addressData?.Complement__c ?? "",
				city: addressData?.City__c ?? "",
				state: addressData?.State__c ?? "",
				country: addressData?.Country__c ?? "",
				postalCodeSecondary: addressData?.Adm_Postal_Code__c ?? "",
				streetSecondary: addressData?.Adm_Address__c ?? "",
				numberSecondary: addressData?.Adm_Street_Number__c ?? "",
				complementSecondary: addressData?.Adm_Complement__c ?? "",
				citySecondary: addressData?.Adm_City__c ?? "",
				stateSecondary: addressData?.Adm_State__c ?? "",
				countrySecondary: addressData?.Adm_Country__c ?? "",
			});

			if (addressRequestDTO.postalCode) {
				Object.entries(addressRequestDTO).forEach(([key, value]) => {
					if (!key.includes("Secondary")) {
						commit("setEstablishmentAddress", {
							index: 0,
							key,
							value,
						});
					}
				});
			}

			if (establishmentRequestDTO.sameAddress === false || establishmentRequestDTO.sameAddress === "false") {
				Object.entries(addressRequestDTO).forEach(([key, value]) => {
					if (key.includes("Secondary")) {
						commit("setEstablishmentAddress", {
							index: 1,
							key: key.replace("Secondary", ""),
							value,
						});
					}
				});
			}

			if (establishmentRequestDTO.hasGroup) {
				commit("setEstablishment", {
					key: "hasGroup",
					value: true,
				});

				const groupRequestDTO = new GroupRequestDTO({
					name: establishmentData?.Corporate_Name__c ?? "",
					taxId: establishmentData?.Corporate_Registration__c ?? "",
				});

				Object.entries(groupRequestDTO).forEach(([key, value]) => {
					commit("setEstablishmentGroup", {
						key,
						value,
					});
				});
			}
		},
		async getContactsData({ commit, state }) {
			let formData = new FormData();

			formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
			formData.append("step", state.step);

			const res = await axios
				.post(baseUrl + "?class=Contacts&method=getContactsData", formData, multipartHeader)
				.catch((err) => {
					this.commit("setShowSalesforceGetError", true);
					this.commit("setLoadingButton", false);

					if (isDevelopment === true) {
						console.error(err);
					}
				});
			if (res.status !== 200) {
				return;
			}

			const contactTypes = [
				"owner",
				"representative",
				"contract",
				"billing",
				"technical",
				"emergency",
				"channel",
				"support",
				"refund",
			];

			const contactsList = res.data;

			if (contactsList.political && contactsList.political.length) {
				commit("clearContacts", "political");
				commit("setEstablishment", {
					key: "exposed",
					value: true,
				});

				contactTypes.push("political");
			}

			contactTypes.forEach((type) => {
				if (contactsList[type] && contactsList[type].length) {
					commit("clearContacts", type);

					contactsList[type].forEach((contact, contactIndex) => {
						let documentOrder = contact?.Document_Order__c || [];

						if (documentOrder) {
							if (typeof documentOrder === 'string') {
								try {
									documentOrder = JSON.parse(documentOrder);
								} catch (e) {
									documentOrder = Array.isArray(documentOrder) ? documentOrder : [documentOrder];
								}
							}

							if (!Array.isArray(documentOrder)) {
								documentOrder = [documentOrder];
							}

						} else {
							documentOrder = [];
						}

						const contactRequestDTO = new ContactRequestDTO({
							id: contact?.Id,
							fullName: contact?.Name,
							nationality: contact?.Nationality__c,
							postalCode: contact?.MailingAddress?.postalCode,
							street: contact?.MailingAddress?.street?.match(/^[^\d]+/)[0],
							number: contact?.MailingAddress?.street?.match(/\d+$/)[0],
							complement: contact?.Complement__c,
							city: contact?.MailingAddress?.city,
							state: contact?.MailingAddress?.state,
							country: contact?.MailingAddress?.country ?? contact?.Nationality__c,
							percent: contact?.Ownership_Percentage__c,
							role: contact?.Role__c,
							email: contact?.Email,
							linkedin: contact?.Linkedin__c,
							cpf: contact?.Registration_Number__c,
							rg: "",
							birthday: contact?.Birthdate?.split("-").reverse().join("/"),
							phone: contact?.Phone,
							url: contact?.Channel_URL__c,
							documentOrder: documentOrder,
						});

						commit("addContactToForm", {
							form: type,
							structure: contactRequestDTO,
						});

						if (type === 'owner' && state.ownersAndContacts.owner[contactIndex]) {
							const existingOwner = state.ownersAndContacts.owner[contactIndex];
							const existingPendingDocumentOrder = existingOwner.pendingDocumentOrder || [];

							const mergedDocumentOrder = [...documentOrder];

							existingPendingDocumentOrder.forEach((pendingDoc) => {
								const alreadyExists = mergedDocumentOrder.some(
									(doc) => {
										const sameName = doc.name === pendingDoc.name;
										const sameOwner = doc.ownerIndex === pendingDoc.ownerIndex ||
											doc.ownerIndex === contactIndex ||
											(!doc.ownerIndex && pendingDoc.ownerIndex === contactIndex);
										return sameName && sameOwner;
									}
								);

								if (!alreadyExists) {
									mergedDocumentOrder.push({
										...pendingDoc,
										ownerIndex: pendingDoc.ownerIndex !== undefined ? pendingDoc.ownerIndex : contactIndex
									});
								}
							});

							state.ownersAndContacts.owner[contactIndex].documentOrder = mergedDocumentOrder;
						} else if (type === 'representative' && state.ownersAndContacts.representative[contactIndex]) {
							const existingRepresentative = state.ownersAndContacts.representative[contactIndex];
							const existingPendingDocumentOrder = existingRepresentative.pendingDocumentOrder || [];

							const mergedDocumentOrder = [...documentOrder];

							existingPendingDocumentOrder.forEach((pendingDoc) => {
								const alreadyExists = mergedDocumentOrder.some(
									(doc) => {
										const sameName = doc.name === pendingDoc.name;
										const sameRepresentative = doc.representativeIndex === pendingDoc.representativeIndex ||
											doc.representativeIndex === contactIndex ||
											(!doc.representativeIndex && pendingDoc.representativeIndex === contactIndex);
										return sameName && sameRepresentative;
									}
								);

								if (!alreadyExists) {
									mergedDocumentOrder.push({
										...pendingDoc,
										representativeIndex: pendingDoc.representativeIndex !== undefined ? pendingDoc.representativeIndex : contactIndex
									});
								}
							});

							state.ownersAndContacts.representative[contactIndex].documentOrder = mergedDocumentOrder;
						}
					});
				}
			});
		},
		async updateStep(fn, step) {

			let formData = new FormData();

			formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
			formData.append("step", step);

			await axios.post(baseUrl + "?class=Personal&method=updateStep", formData, multipartHeader);
		},
		async getIntegrationData({ commit, state }) {
			let formData = new FormData();

			formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
			formData.append("step", state.step);

			const res = await axios
				.post(baseUrl + "?class=Integration&method=getIntegrationData", formData, multipartHeader)
				.catch((err) => {
					this.commit("setShowSalesforceGetError", true);
					this.commit("setLoadingButton", false);

					if (isDevelopment === true) {
						console.error(err);
					}
				});

			if (res.status !== 200) {
				return;
			}

			const integration = integrations.filter((int) => {
				return int.text === res.data["Integration_Type__c"];
			})[0];

			commit("setPci", {
				key: "integration",
				value: integration,
			});

			commit("setPci", {
				key: "conformity",
				value: res.data["conformity"] == 1 ? true : false,
			});
		},
		async getUploadedFiles({ commit, state }) {
			let formData = new FormData();

			formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
			formData.append("step", state.step);

			const res = await axios
				.post(baseUrl + "?class=Files&method=getDocuments", formData, multipartHeader)
				.catch((err) => {
					this.commit("setShowSalesforceGetError", true);
					this.commit("setLoadingButton", false);

					if (isDevelopment === true) {
						console.error(err);
					}
				});

			if (res.status !== 200) {
				return;
			}

			if (res.data.error) {
				return;
			}

			res.data.forEach((file) => {
				const fileObj = {
					value: {
						form: file.ContentDocument.Title.split(" ")[0],
						name: file.ContentDocument.LatestPublishedVersion.PathOnClient,
						salesforceFileId: file.ContentDocumentId,
					},
				};

				try {
					this.commit("addUploadedDocument", fileObj);
				} catch (err) {
					this.commit("setShowSalesforceGetError", true);
					this.commit("setLoadingButton", false);

					if (isDevelopment === true) {
						console.error(err);
					}
				}
			});
		},
		async getBankAccountData({ commit, state }) {
			let formData = new FormData();

			formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
			formData.append("step", state.step);

			const res = await axios
				.post(baseUrl + "?class=Bank&method=getBankAccount", formData, multipartHeader)
				.catch((err) => {
					this.commit("setShowSalesforceGetError", true);
					this.commit("setLoadingButton", false);

					if (isDevelopment === true) {
						console.error(err);
					}
				});

			if (res.status !== 200) {
				return;
			}

			const bankData = res.data;

			const bankRequestDTO = new BankRequestDTO({
				name: bankData?.Bank_Account_Owner__c ?? "",
				taxId: bankData?.Bank_Account_Document__c ?? "",
				bank: bankData?.Bank_Name__c ?? "",
				number: bankData?.Bank_Number__c ?? "",
				agency: bankData?.Bank_Agency__c ?? "",
				account: bankData?.Bank_Account__c ?? "",
				selectedBank: "",
				step: bankData?.Stage_Number__c ?? "",
			});

			Object.entries(bankRequestDTO).forEach(([key, value]) => {
				commit("setBankAccount", { key, value });
			});
		},
		addContact({ commit }, key) {
			let structure;

			switch (key) {
				case "owner":
					structure = ownersStructure;
					break;
				case "representative":
					structure = representativeStructure;
					break;
				case "contract":
					structure = contractStructure;
					break;
				case "political":
					structure = politicalStructure;
					break;
				default:
					structure = contactStructure;
					break;
			}

			commit("addContactToForm", { form: key, structure });
		},
		removeContact({ commit }) {
			commit("removeContactToForm");
		},
		removeDocument({ commit }) {
			commit("removeDocumentToForm");
		},
		updateContacts({ commit }, { form, value }) {
			commit("updateContactsToForm", { form, value });
		},
		addPendingDelete(context, { file, fileType, loopIndex }) {
			const form = fileTypeToFormKey(fileType);

			if (!context.state.pendingDeletes[form]) {
				context.state.pendingDeletes[form] = [];
			}

			const deleteInfo = {
				file: file,
				fileType: fileType,
				loopIndex: loopIndex,
				salesforceFileId: file.salesforceFileId || file.id,
				name: file.name || file.filename,
			};

			context.state.pendingDeletes[form].push(deleteInfo);

			if ((fileType === "cpf" || fileType === "address_partners") && loopIndex !== undefined && loopIndex !== null) {
				const owner = context.state.ownersAndContacts.owner[loopIndex];
				if (owner && owner.documentOrder) {
					const docIndex = owner.documentOrder.findIndex(
						(doc) => (doc.salesforceFileId === deleteInfo.salesforceFileId || doc.id === deleteInfo.salesforceFileId) &&
							(doc.ownerIndex === loopIndex || doc.ownerIndex === undefined)
					);
					if (docIndex !== -1) {
						owner.documentOrder.splice(docIndex, 1);
					}
				}
			} else if (fileType === "representative" && loopIndex !== undefined && loopIndex !== null) {
				const representative = context.state.ownersAndContacts.representative[loopIndex];
				if (representative && representative.documentOrder) {
					const docIndex = representative.documentOrder.findIndex(
						(doc) => (doc.salesforceFileId === deleteInfo.salesforceFileId || doc.id === deleteInfo.salesforceFileId) &&
							(doc.representativeIndex === loopIndex || doc.representativeIndex === undefined)
					);
					if (docIndex !== -1) {
						representative.documentOrder.splice(docIndex, 1);
					}
				}
			}

			const uploadedIndex = context.state.uploadedDocuments[form].findIndex(
				(doc) => doc.salesforceFileId === deleteInfo.salesforceFileId || doc.id === deleteInfo.salesforceFileId
			);
			if (uploadedIndex !== -1) {
				context.state.uploadedDocuments[form].splice(uploadedIndex, 1);
			}

			return true;
		},
		removePendingDelete(context, { file, fileType, loopIndex }) {
			const form = fileTypeToFormKey(fileType);

			if (!context.state.pendingDeletes[form]) {
				return;
			}

			const salesforceFileId = file.salesforceFileId || file.id;
			const index = context.state.pendingDeletes[form].findIndex(
				(pendingDelete) => (pendingDelete.salesforceFileId === salesforceFileId || pendingDelete.file?.salesforceFileId === salesforceFileId)
			);

			if (index !== -1) {
				context.state.pendingDeletes[form].splice(index, 1);
			}
		},
		async deletePendingFiles(context) {
			const pendingDeletes = context.state.pendingDeletes;
			let allSuccess = true;

			for (const formKey in pendingDeletes) {
				const files = [...pendingDeletes[formKey]];

				if (files.length === 0) {
					continue;
				}

				const successfullyDeleted = [];

				for (const deleteInfo of files) {
					const formData = new FormData();
					formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
					formData.append("fid", deleteInfo.file?.id || "");
					formData.append("form", deleteInfo.fileType);
					formData.append("step", context.state.step);
					formData.append("salesforceFileId", deleteInfo.salesforceFileId);

					try {
						const res = await axios.post(
							baseUrl + "?class=Files&method=deleteDocument",
							formData,
							multipartHeader
						);

						if (res.status === 200) {
							successfullyDeleted.push(deleteInfo);
						} else {
							allSuccess = false;
						}
					} catch (error) {
						allSuccess = false;
						if (isDevelopment === true) {
							console.error("Error deleting pending file:", error);
						}
					}
				}

				context.state.pendingDeletes[formKey] = context.state.pendingDeletes[formKey].filter(
					(pendingDelete) => !successfullyDeleted.some((deleted) => deleted.salesforceFileId === pendingDelete.salesforceFileId)
				);
			}

			return allSuccess;
		},
		async deleteDocumentFromServer(context, file) {
			const fileType = file.form || file.fileType;

			if (!fileType) {
				return false;
			}

			return context.dispatch("addPendingDelete", {
				file: file,
				fileType: fileType,
				loopIndex: file.loopIndex !== undefined ? file.loopIndex : null,
			});
		},
		addPendingFile(context, { file, fileType, loopIndex }) {
			const form = fileTypeToFormKey(fileType);

			const originalFileName = file.name;
			let uniqueFileName = originalFileName;

			if ((fileType === "cpf" || fileType === "address_partners") && loopIndex !== undefined && loopIndex !== null) {
				const owner = context.state.ownersAndContacts.owner[loopIndex];
				if (owner) {
					const documentOrder = owner.documentOrder || [];
					const pendingDocumentOrder = owner.pendingDocumentOrder || [];

					// Verifica também nos arquivos já enviados (uploadedDocuments) do mesmo tipo
					const uploadedFiles = context.state.uploadedDocuments[form] || [];
					const uploadedFilesForOwner = uploadedFiles.filter(
						(uploadedFile) => {
							// Verifica se o arquivo tem o mesmo form e pertence a este owner
							const fileForm = uploadedFile.form || fileType;
							if (fileForm !== fileType) return false;

							// Verifica se está no documentOrder deste owner
							return documentOrder.some(
								(doc) => (doc.salesforceFileId === uploadedFile.salesforceFileId || doc.id === uploadedFile.salesforceFileId) &&
									(doc.ownerIndex === loopIndex || doc.ownerIndex === undefined) &&
									(doc.form === fileType)
							);
						}
					);

					// Combina todos os arquivos existentes para verificar duplicatas
					const allExistingFiles = [
						...documentOrder.filter(doc =>
							(doc.ownerIndex === loopIndex || doc.ownerIndex === undefined) &&
							(doc.form === "cpf" || doc.form === "address_partners")
						),
						...pendingDocumentOrder.filter(doc =>
							doc.ownerIndex === loopIndex &&
							(doc.form === "cpf" || doc.form === "address_partners")
						),
						...uploadedFilesForOwner
					];

					// Gera nome único se necessário
					uniqueFileName = generateUniqueFileName(originalFileName, allExistingFiles, (item) => {
						return item.name || item.filename;
					});
				}
			} else if (fileType === "representative" && loopIndex !== undefined && loopIndex !== null) {
				const representative = context.state.ownersAndContacts.representative[loopIndex];
				if (representative) {
					const documentOrder = representative.documentOrder || [];
					const pendingDocumentOrder = representative.pendingDocumentOrder || [];

					// Verifica também nos arquivos já enviados (uploadedDocuments) do mesmo tipo
					const uploadedFiles = context.state.uploadedDocuments[form] || [];
					const uploadedFilesForRepresentative = uploadedFiles.filter(
						(uploadedFile) => {
							// Verifica se o arquivo tem o mesmo form e pertence a este representative
							const fileForm = uploadedFile.form || fileType;
							if (fileForm !== fileType) return false;

							// Verifica se está no documentOrder deste representative
							return documentOrder.some(
								(doc) => (doc.salesforceFileId === uploadedFile.salesforceFileId || doc.id === uploadedFile.salesforceFileId) &&
									(doc.representativeIndex === loopIndex || doc.representativeIndex === undefined) &&
									(doc.form === fileType)
							);
						}
					);

					// Combina todos os arquivos existentes para verificar duplicatas
					const allExistingFiles = [
						...documentOrder.filter(doc =>
							(doc.representativeIndex === loopIndex || doc.representativeIndex === undefined) &&
							(doc.form === "representative")
						),
						...pendingDocumentOrder.filter(doc =>
							doc.representativeIndex === loopIndex &&
							(doc.form === "representative")
						),
						...uploadedFilesForRepresentative
					];

					// Gera nome único se necessário
					uniqueFileName = generateUniqueFileName(originalFileName, allExistingFiles, (item) => {
						return item.name || item.filename;
					});
				}
			} else {
				const existingPendingFiles = context.state.pendingFiles[form] || [];
				const uploadedFiles = context.state.uploadedDocuments[form] || [];

				// Combina arquivos pendentes e já enviados do mesmo tipo
				const allExistingFiles = [
					...existingPendingFiles.filter(pf => pf.fileType === fileType),
					...uploadedFiles.filter(uf => (uf.form || fileType) === fileType)
				];

				// Gera nome único se necessário
				uniqueFileName = generateUniqueFileName(originalFileName, allExistingFiles, (item) => {
					return item.name || item.filename;
				});
			}

			// Cria um novo File object com o nome único se foi renomeado
			let fileToAdd = file;
			if (uniqueFileName !== originalFileName) {
				// Cria um novo File com o nome modificado
				const renamedFile = new File([file], uniqueFileName, {
					type: file.type,
					lastModified: file.lastModified
				});
				fileToAdd = renamedFile;
			}

			const pendingFile = {
				file: fileToAdd,
				fileType: fileType,
				loopIndex: loopIndex,
				name: uniqueFileName,
			};

			context.state.pendingFiles[form].push(pendingFile);

			if ((fileType === "cpf" || fileType === "address_partners") && loopIndex !== undefined && loopIndex !== null) {
				const owner = context.state.ownersAndContacts.owner[loopIndex];
				if (owner) {
					if (!owner.pendingDocumentOrder) {
						owner.pendingDocumentOrder = [];
					}
					owner.pendingDocumentOrder.push({
						name: uniqueFileName,
						ownerIndex: loopIndex,
						form: fileType,
					});
				}
			} else if (fileType === "representative" && loopIndex !== undefined && loopIndex !== null) {
				const representative = context.state.ownersAndContacts.representative[loopIndex];
				if (representative) {
					if (!representative.pendingDocumentOrder) {
						representative.pendingDocumentOrder = [];
					}
					representative.pendingDocumentOrder.push({
						name: uniqueFileName,
						representativeIndex: loopIndex,
						form: fileType,
					});
				}
			}

			return true;
		},
		removePendingFile(context, { file, fileType, loopIndex }) {
			const form = fileTypeToFormKey(fileType);

			const index = context.state.pendingFiles[form].findIndex(
				(pendingFile) => pendingFile.name === file.name && pendingFile.loopIndex === loopIndex
			);

			if (index !== -1) {
				context.state.pendingFiles[form].splice(index, 1);
			}

			if ((fileType === "cpf" || fileType === "address_partners") && loopIndex !== undefined && loopIndex !== null) {
				const owner = context.state.ownersAndContacts.owner[loopIndex];
				if (owner && owner.pendingDocumentOrder) {
					const docIndex = owner.pendingDocumentOrder.findIndex(
						(item) => item.name === file.name && item.ownerIndex === loopIndex
					);
					if (docIndex !== -1) {
						owner.pendingDocumentOrder.splice(docIndex, 1);
					}
				}
			} else if (fileType === "representative" && loopIndex !== undefined && loopIndex !== null) {
				const representative = context.state.ownersAndContacts.representative[loopIndex];
				if (representative && representative.pendingDocumentOrder) {
					const docIndex = representative.pendingDocumentOrder.findIndex(
						(item) => item.name === file.name && item.representativeIndex === loopIndex
					);
					if (docIndex !== -1) {
						representative.pendingDocumentOrder.splice(docIndex, 1);
					}
				}
			}
		},
		async sendPendingFiles(context) {
			const pendingFiles = context.state.pendingFiles;
			let allSuccess = true;

			for (const formKey in pendingFiles) {
				const files = [...pendingFiles[formKey]];

				if (files.length === 0) {
					continue;
				}

				const successfullySent = [];

				for (const pendingFile of files) {
					const fileName = pendingFile.name || pendingFile.file.name;
					let alreadySent = false;

					if ((pendingFile.fileType === "cpf" || pendingFile.fileType === "address_partners") &&
						pendingFile.loopIndex !== undefined && pendingFile.loopIndex !== null) {
						const owner = context.state.ownersAndContacts.owner[pendingFile.loopIndex];
						if (owner) {
							const documentOrder = owner.documentOrder || [];
							// Verifica no documentOrder se o arquivo já foi enviado
							alreadySent = documentOrder.some(
								(doc) => doc.name === fileName &&
									(doc.salesforceFileId || doc.id) &&
									(doc.ownerIndex === pendingFile.loopIndex || doc.ownerIndex === undefined) &&
									(doc.form === pendingFile.fileType || !doc.form)
							);

							// Se não encontrou no documentOrder, verifica também nos uploadedDocuments
							if (!alreadySent) {
								const uploadedDocs = context.state.uploadedDocuments[formKey] || [];
								alreadySent = uploadedDocs.some(
									(doc) => {
										const docName = doc.name || doc.filename;
										const docForm = doc.form || pendingFile.fileType;
										return docName === fileName &&
											docForm === pendingFile.fileType &&
											(doc.salesforceFileId || doc.id);
									}
								);
							}
						}
					} else if (pendingFile.fileType === "representative" &&
						pendingFile.loopIndex !== undefined && pendingFile.loopIndex !== null) {
						const representative = context.state.ownersAndContacts.representative[pendingFile.loopIndex];
						if (representative) {
							const documentOrder = representative.documentOrder || [];
							// Verifica no documentOrder se o arquivo já foi enviado
							alreadySent = documentOrder.some(
								(doc) => doc.name === fileName &&
									(doc.salesforceFileId || doc.id) &&
									(doc.representativeIndex === pendingFile.loopIndex || doc.representativeIndex === undefined) &&
									(doc.form === pendingFile.fileType || !doc.form)
							);

							// Se não encontrou no documentOrder, verifica também nos uploadedDocuments
							if (!alreadySent) {
								const uploadedDocs = context.state.uploadedDocuments[formKey] || [];
								alreadySent = uploadedDocs.some(
									(doc) => {
										const docName = doc.name || doc.filename;
										const docForm = doc.form || pendingFile.fileType;
										return docName === fileName &&
											docForm === pendingFile.fileType &&
											(doc.salesforceFileId || doc.id);
									}
								);
							}
						}
					} else {
						// Para arquivos sem loopIndex, verifica no uploadedDocuments
						// IMPORTANTE: Verifica pelo nome E pelo tipo de formulário
						// Isso permite que arquivos com mesmo nome sejam salvos em tipos diferentes
						const uploadedDocs = context.state.uploadedDocuments[formKey] || [];

						alreadySent = uploadedDocs.some(
							(doc) => {
								const docName = doc.name || doc.filename;
								// O doc.form vem do título do Salesforce (ex: "taxId", "contract")
								// O pendingFile.fileType é o tipo original (ex: "taxId", "contract", "social_contract")
								const docForm = doc.form;
								const expectedForm = pendingFile.fileType;

								// Verifica se o nome corresponde E se o tipo de formulário corresponde
								// Permite arquivos com mesmo nome em tipos diferentes
								return docName === fileName &&
									// Verifica se o tipo corresponde (considera mapeamentos)
									(docForm === expectedForm ||
										(expectedForm === "social_contract" && docForm === "socialContract") ||
										(expectedForm === "second_address" && docForm === "secondAddress") ||
										(expectedForm === "address_partners" && docForm === "addressPartners") ||
										(expectedForm === "cnpj" && docForm === "taxId")) &&
									(doc.salesforceFileId || doc.id);
							}
						);
					}

					if (alreadySent) {
						successfullySent.push(pendingFile);
						continue;
					}

					const formData = new FormData();
					formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
					formData.append("form", pendingFile.fileType);
					formData.append("step", context.state.step);
					formData.append("file", pendingFile.file);

					try {
						const res = await axios.post(
							baseUrl + "?class=Files&method=saveDocument",
							formData,
							multipartHeader
						);

						if (res.status === 200 && res.data && res.data.success) {
							const contentDocumentId = res.data.contentDocumentId;

							// Se o arquivo já existia, apenas marca como enviado sem duplicar
							if (res.data.alreadyExists && contentDocumentId) {
								// Verifica se já está no uploadedDocuments antes de adicionar
								const uploadedDocs = context.state.uploadedDocuments[formKey] || [];
								const alreadyInUploaded = uploadedDocs.some(
									(doc) => {
										const docId = doc.salesforceFileId || doc.id;
										return docId === contentDocumentId;
									}
								);

								if (!alreadyInUploaded) {
									context.state.uploadedDocuments[formKey].push({
										filename: pendingFile.file.name,
										form: pendingFile.fileType,
										name: pendingFile.file.name,
										salesforceFileId: contentDocumentId,
									});
								}

								successfullySent.push(pendingFile);

								// Atualiza o documentOrder se necessário
								if ((pendingFile.fileType === "cpf" || pendingFile.fileType === "address_partners") &&
									pendingFile.loopIndex !== undefined && pendingFile.loopIndex !== null) {
									const owner = context.state.ownersAndContacts.owner[pendingFile.loopIndex];
									if (owner) {
										if (!owner.documentOrder) {
											owner.documentOrder = [];
										}

										const existingDocIndex = owner.documentOrder.findIndex(
											(doc) => doc.name === fileName &&
												(doc.ownerIndex === pendingFile.loopIndex || doc.ownerIndex === undefined) &&
												(doc.form === pendingFile.fileType || !doc.form)
										);

										if (existingDocIndex === -1) {
											owner.documentOrder.push({
												name: pendingFile.file.name,
												ownerIndex: pendingFile.loopIndex,
												form: pendingFile.fileType,
												salesforceFileId: contentDocumentId,
											});
										} else if (!owner.documentOrder[existingDocIndex].salesforceFileId) {
											owner.documentOrder[existingDocIndex].salesforceFileId = contentDocumentId;
										}
									}
								} else if (pendingFile.fileType === "representative" &&
									pendingFile.loopIndex !== undefined && pendingFile.loopIndex !== null) {
									const representative = context.state.ownersAndContacts.representative[pendingFile.loopIndex];
									if (representative) {
										if (!representative.documentOrder) {
											representative.documentOrder = [];
										}

										const existingDocIndex = representative.documentOrder.findIndex(
											(doc) => doc.name === fileName &&
												(doc.representativeIndex === pendingFile.loopIndex || doc.representativeIndex === undefined) &&
												(doc.form === pendingFile.fileType || !doc.form)
										);

										if (existingDocIndex === -1) {
											representative.documentOrder.push({
												name: pendingFile.file.name,
												representativeIndex: pendingFile.loopIndex,
												form: pendingFile.fileType,
												salesforceFileId: contentDocumentId,
											});
										} else if (!representative.documentOrder[existingDocIndex].salesforceFileId) {
											representative.documentOrder[existingDocIndex].salesforceFileId = contentDocumentId;
										}
									}
								}

								continue;
							}

							context.state.uploadedDocuments[formKey].push({
								filename: pendingFile.file.name,
								form: pendingFile.fileType,
								name: pendingFile.file.name,
								salesforceFileId: contentDocumentId,
							});

							if ((pendingFile.fileType === "cpf" || pendingFile.fileType === "address_partners") &&
								pendingFile.loopIndex !== undefined && pendingFile.loopIndex !== null) {
								const owner = context.state.ownersAndContacts.owner[pendingFile.loopIndex];
								if (owner) {
									if (!owner.documentOrder) {
										owner.documentOrder = [];
									}

									const existingDocIndex = owner.documentOrder.findIndex(
										(doc) => doc.name === fileName &&
											!doc.salesforceFileId && !doc.id &&
											(doc.ownerIndex === pendingFile.loopIndex || doc.ownerIndex === undefined)
									);

									if (existingDocIndex !== -1) {
										owner.documentOrder[existingDocIndex] = {
											name: pendingFile.file.name,
											ownerIndex: pendingFile.loopIndex,
											form: pendingFile.fileType,
											salesforceFileId: contentDocumentId,
										};
									} else {
										owner.documentOrder.push({
											name: pendingFile.file.name,
											ownerIndex: pendingFile.loopIndex,
											form: pendingFile.fileType,
											salesforceFileId: contentDocumentId,
										});
									}

									if (owner.pendingDocumentOrder) {
										const pendingIndex = owner.pendingDocumentOrder.findIndex(
											(item) => item.name === pendingFile.file.name && item.ownerIndex === pendingFile.loopIndex
										);
										if (pendingIndex !== -1) {
											owner.pendingDocumentOrder.splice(pendingIndex, 1);
										}
									}
								}
							} else if (pendingFile.fileType === "representative" &&
								pendingFile.loopIndex !== undefined && pendingFile.loopIndex !== null) {
								const representative = context.state.ownersAndContacts.representative[pendingFile.loopIndex];
								if (representative) {
									if (!representative.documentOrder) {
										representative.documentOrder = [];
									}

									const existingDocIndex = representative.documentOrder.findIndex(
										(doc) => doc.name === fileName &&
											!doc.salesforceFileId && !doc.id &&
											(doc.representativeIndex === pendingFile.loopIndex || doc.representativeIndex === undefined)
									);

									if (existingDocIndex !== -1) {
										representative.documentOrder[existingDocIndex] = {
											name: pendingFile.file.name,
											representativeIndex: pendingFile.loopIndex,
											form: pendingFile.fileType,
											salesforceFileId: contentDocumentId,
										};
									} else {
										representative.documentOrder.push({
											name: pendingFile.file.name,
											representativeIndex: pendingFile.loopIndex,
											form: pendingFile.fileType,
											salesforceFileId: contentDocumentId,
										});
									}

									if (representative.pendingDocumentOrder) {
										const pendingIndex = representative.pendingDocumentOrder.findIndex(
											(item) => item.name === pendingFile.file.name && item.representativeIndex === pendingFile.loopIndex
										);
										if (pendingIndex !== -1) {
											representative.pendingDocumentOrder.splice(pendingIndex, 1);
										}
									}
								}
							}

							successfullySent.push(pendingFile);
						} else {
							allSuccess = false;
						}
					} catch (error) {
						allSuccess = false;
						if (isDevelopment === true) {
							console.error("Error sending pending file:", error);
						}
					}
				}

				context.state.pendingFiles[formKey] = context.state.pendingFiles[formKey].filter(
					(pendingFile) => !successfullySent.some((sent) => sent.name === pendingFile.name && sent.loopIndex === pendingFile.loopIndex)
				);
			}

			return allSuccess;
		},
		sendFile(context, [files, fileType, loopIndex]) {
			context.commit("setLoadingButton", true);

			let success;
			const form = fileTypeToFormKey(fileType);

			let formData = new FormData();

			formData.append("kyc_uuid", sessionStorage.getItem("kyc_uuid"));
			formData.append("form", fileType);
			formData.append("step", context.state.step);

			files.forEach((file) => {
				formData.append("file", file);
				success = axios
					.post(baseUrl + "?class=Files&method=saveDocument", formData, multipartHeader)
					.then((res) => {
						if (res.status === 200 && res.data && res.data.success) {
							const contentDocumentId = res.data.contentDocumentId;

							context.state.uploadedDocuments[form].push({
								filename: file.name,
								form: fileType,
								name: file.name,
								salesforceFileId: contentDocumentId,
							});

							if ((fileType === "cpf" || fileType === "address_partners") && loopIndex !== undefined && loopIndex !== null) {
								const owner = context.state.ownersAndContacts.owner[loopIndex];
								if (owner) {
									if (!owner.documentOrder) {
										owner.documentOrder = [];
									}
									owner.documentOrder.push({
										name: file.name,
										ownerIndex: loopIndex,
										form: fileType,
										salesforceFileId: contentDocumentId,
									});
								}
							}

							context.commit("setLoadingButton", false);
							return true;
						}

						context.commit("setLoadingButton", false);
						return false;
					})
					.catch(() => {

						context.state.uploadedDocuments[form] = [];
						context.state.documents[form] = [];

						context.commit("setLoadingButton", false);
						return false;
					});
			});

			return success;
		},
	},
	mutations: {
		setLoadingButton(state, data) {
			state.loadingButton = data;
		},
		setStep(state, step) {
			state.step = step;
		},
		setPersonal(state, { key, value }) {
			state.personal[key] = value;
		},
		setEstablishment(state, { key, value }) {
			state.establishment[key] = value;
		},
		setEstablishmentGroup(state, { key, value }) {
			state.establishment.group[key] = value;
		},
		setEstablishmentAddress(state, { index, key, value }) {
			state.establishment.address[index][key] = value;
		},
		setBankAccount(state, { key, value }) {
			state.bankAccount[key] = value;
		},
		setContactData(state, { form, index, key, value }) {
			if (form == "channel" && index > 0) {
				state.ownersAndContacts[form][index]["fullname"] = "-";
			}

			state.ownersAndContacts[form][index][key] = value;
		},
		setPci(state, { key, value }) {
			state.pci[key] = value;
		},
		setDocuments(state, { form, value }) {
			state.documents[form] = value;
		},
		addUploadedDocument(state, file) {
			let formKey = fileTypeToFormKey(file.value.form);
			if (formKey === "cnpj") {
				formKey = "taxId";
			}
			if (formKey === "KYC") {
				return;
			}
			state.uploadedDocuments[formKey].push({ ...file.value, form: formKey });

		},
		addContactToForm(state, { form, structure }) {
			let key = state.ownersAndContacts[form].length;

			while (state.ownersAndContacts[form].indexOf(key) !== -1) {
				key++;
			}

			state.ownersAndContacts[form].push(cloneObject(structure));
		},
		clearContacts(state, form) {
			state.ownersAndContacts[form] = [];
		},
		async removeContactToForm(state) {
			if (state.ownersAndContacts[state.confirmModal.form]?.[state.confirmModal.formKey]) {
				state.ownersAndContacts[state.confirmModal.form].splice(state.confirmModal.formKey, 1);
			}

			let formData = new FormData();

			formData.append("salesforce_contact_id", state.confirmModal.salesforceContactId);

			if (state.confirmModal.salesforceContactId) {
				const res = await axios
					.post(baseUrl + "?class=Contacts&method=deleteContact", formData, multipartHeader)
					.catch((err) => {
						if (isDevelopment === true) {
							console.error(err);
						}
					});
			}
		},
		removeDocumentToForm(state) {
			const formIndex = state.confirmModal.form;

			// A deleção real é feita pela action deleteDocumentFromServer que chama addPendingDelete
			// Esta mutation apenas limpa o array de documentos locais se necessário

			// Arquivo contido no input do respectivo índice
			state.documents[formIndex] = [];
		},
		updateContactsToForm(state, { form, value }) {
			state.ownersAndContacts[form] = value;
		},
		setConfirmForm(state, { form, key, type = false, formKey, salesforceContactId }) {
			state.confirmModal.type = type;
			state.confirmModal.form = form;
			state.confirmModal.key = key;

			if (formKey) {
				state.confirmModal.formKey = formKey;
			}

			if (salesforceContactId) {
				state.confirmModal.salesforceContactId = salesforceContactId;
			}
		},
		setShowSalesforceGetError(state, value) {
			state.showSalesforceGetError = value;
		},
		setShowSalesforcePostError(state, value) {
			state.showSalesforcePostError = value;
		},
		setSalesforcePostErrorMessage(state, value) {
			state.salesforcePostErrorMessage = value;
		},
	},
};

export default module;
